import { Box, Stack, Typography } from '@mui/material';
import {
  PRE_CONSULTATION_ORIGIN_START_POSITION,
  SCORES_FIELD_KEYS,
  SECTION_KEY_VALUES,
  SurveillanceTable,
  useSurveillanceEventData,
} from '@parsys/frontend_library';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getChoiceLabelFromFieldValue } from '../utils/getChoiceLabelFromFieldValue';
import { getPreConsultationOriginsForWorkflow } from '../utils/getPreConsultationOrigin';
import { selectSurveillanceDataForCurrentConsultation } from 'FRONTEND/reducers/consultationSlice';
import { selectConsultationTenantId } from 'FRONTEND/selectors/consultationSelectors';
import { selectConsultationExamForm } from 'FRONTEND/selectors/workflowSelectors';
import { surveillanceDataColor } from 'FRONTEND/utils/constants';
import { useMedicineList } from 'FRONTEND/utils/hooks/useMedicineList';
import i18n from 'FRONTEND/utils/i18n';
import { useCurrentWorkflow } from 'FRONTEND/utils/nonSerializableStorage/useCurrentWorkflow';

// TODO: this map is currently used as a fallback for any raw label that does not have a specific translation in the frontend_library (e.g. scores, grouped values, section labels, exam-ID-like labels, etc.).
// We should find a more efficient way to handle translations for these different types of labels instead of having this big map with various types of labels and translations.
const RAW_LABEL_TO_TRANSLATION_MAP = {
  surveillance_glasgow_results: i18n._('Glasgow'),
  surveillance_pulse_rate: i18n._('PR'),
  surveillance_spo2: i18n._('SpO2'),
  surveillance_o2_therapie_debit: i18n._('Flow (O2)'),
  surveillance_o2_therapie_fio2: i18n._('FiO2 (O2)'),
  surveillance_etco2: i18n._('EtCO2'),
  surveillance_vi_etco2: i18n._('EtCO2 (VI)'),
  surveillance_vi_freq_respiratoire: i18n._('RF (VI)'),
  surveillance_fio2: i18n._('FiO2 (VI)'),
  surveillance_vi_peep: i18n._('PEEP (VI)'),
  surveillance_vi_volume_courant: i18n._('CV (VI)'),
  surveillance_vi_pression_inspiratoire: i18n._('IP (VI)'),
  surveillance_vni_fio2: i18n._('FiO2 (VNI)'),
  surveillance_vni_peep: i18n._('PEEP (VNI)'),
  surveillance_vni_al: i18n._('Al (VNI)'),
  surveillance_glucose: i18n._('Blood sugar'),
  surveillance_temperature: i18n._('Temperature'),
  surveillance_echelle_douleur: i18n._('EVA'),
  surveillance_sao2: i18n._('SaO2'),
  surveillance_spco: i18n._('SpCO'),
  surveillance_acte_medical_realise: i18n._('Acts'),
  surveillance_score: i18n._('Scores'),
  surveillance_notes: i18n._('Medical developments'),
  documentAdded: i18n._('Document added'),
  conditioning: i18n._('Conditioning'),
  clinicalExamination: i18n._('Clinical Examination'),
  surveillance_freq_respiratoire: i18n._('RF'),
  surveillance_oxygen_flow_rate_spo2: i18n._('Flow (O2)'),
  surveillance_oxygen_air_ambiant: i18n._('Ambient air'),
  surveillance_liste_type_trauma: i18n._('Severe Trauma'),
  surveillance_vital_hemoglobine: i18n._('Hémocue'),
  // Administration routes
  cutanee: i18n._('Cutaneous'),
  inhalation: i18n._('Inhalation'),
  ivd: i18n._('IVD'),
  ivl: i18n._('IVL'),
  ivse: i18n._('IVSE'),
  per_os: i18n._('Per Os'),
  intra_rectal: i18n._('Intrarectal'),
  intra_musculaire: i18n._('Intramuscular'),
  intra_nasal: i18n._('Intranasal'),
  solute: i18n._('Solute'),
  transdermique: i18n._('Transdermal'),
};

// TODO: make this depend on @parsys/frontend_library's SECTION_KEY_MAP somehow
const SECTION_LABEL_TRANSLATION_MAP = {
  section_constants: i18n._('Vitals section'),
  section_therapeutics: i18n._('Therapeutics section'),
  section_acts: i18n._('Acts section'),
};


const GROUPED_VALUES = {
  arterial_pressure: (
    <Stack paddingX={1} gap={1}>
      <Box
        component='span'
        sx={{ color: surveillanceDataColor.SYS }}
      >PAS
      </Box>
      <Box
        component='span'
        sx={{ color: surveillanceDataColor.DIA }}
      > PAD
      </Box>
      <Box
        component='span'
        sx={{ color: surveillanceDataColor.MAP }}
      > PAM
      </Box>
    </Stack>),
};
const GROUPED_VALUES_KEYS = Object.keys(GROUPED_VALUES);

const SurveillanceTableDisplay = ({ onEventClick = () => {} }) => {
  const surveillanceList = useSelector(selectSurveillanceDataForCurrentConsultation);
  const currentWorkflow = useCurrentWorkflow();

  const preConsultationOrigins = useMemo(() => getPreConsultationOriginsForWorkflow(currentWorkflow), [currentWorkflow]);
  const medicineExamName = currentWorkflow.medicineExam || 'smur_idf/medicine'; // Default to smur_idf/medicine for backwards compatibility, but this should be defined in the workflow to avoid hardcoding
  const medicineFormContent = useSelector(state => selectConsultationExamForm(state, currentWorkflow, medicineExamName));
  const currentConsultationTenantId = useSelector(selectConsultationTenantId);
  const medicineList = useMedicineList(medicineFormContent, medicineExamName, currentConsultationTenantId);


  const groupedValues = currentWorkflow?.surveillanceGroupedValues;
  const groupedValuesKeys = groupedValues ? Object.keys(groupedValues) : undefined;
  const groupedValuesSet = groupedValues ? new Set(Object.values(groupedValues)) : undefined;

  const scoreResultFieldMap = currentWorkflow?.surveillanceScoreResultFieldMap;
  const scoreResultFieldIds = scoreResultFieldMap ? Object.keys(scoreResultFieldMap) : undefined;

  const [data, formattedSurveillanceData] = useSurveillanceEventData({
    surveillanceEvents: surveillanceList,
    interval: 1,
    medicineList,
    preConsultationOrigins,
    genericFieldPaths: currentWorkflow.genericFieldPaths,
    groupedValuesKeys, // determines which values are grouped together
    groupedValuesSet, // determines which values are grouped together
    scoreFieldValues: scoreResultFieldIds, // determines which score field ids should be considered score results for validation purpose
    ignoreIntervalKeys: currentWorkflow?.surveillanceIgnoreIntervalKeys, // determines which values should ignore the predetermined interval (default 1min)
    sectionsMap: currentWorkflow?.surveillanceSections, // determines the sections (if any) where each fields are positionned in the surveillance table
    surveillanceKeysToDisplay: currentWorkflow?.surveillanceFieldKeysToDisplay, // determines the field ids to be displayed in the SurveillanceTable
  });

  if (!surveillanceList.length) {
    return (
      <Stack justifyContent='center' alignItems='center'>
        <Typography sx={{ fontSize: '16px' }}>{i18n._('Your actions during the procedure will appear here.')}</Typography>
      </Stack>
    );
  }

  // TODO: find a more clever way for the frontend_library to get the right translation for the labels instead of having this big function with multiple conditionals.
  // The function is currently called in various context for very different types of labels (or even form values).
  // Passing one translate function for each type of label (e.g. one for scores, one for grouped values, one for raw labels, etc.) would be more efficient and easier to maintain.
  const translateTableLabel = (rawLabel, interpolation, fieldPath) => {
    // If the fieldPath is provided, it means we must find the corresponding label in the field choice list.
    if (fieldPath) {
      return getChoiceLabelFromFieldValue({
        fieldId: fieldPath.fieldId,
        fieldValue: rawLabel,
        sectionId: '',
        workflow: currentWorkflow,
        formId: fieldPath.parentExamId,
      });
    }
    // If the rawLabel corresponds to a score, we return the generic 'Score' translation.
    if (SCORES_FIELD_KEYS.includes(rawLabel)) {
      return i18n._('Score');
    }
    // For some specific grouped values (e.g. arterial pressure), we return a custom label with the different sub-values.
    if (GROUPED_VALUES_KEYS.includes(rawLabel)) {
      return GROUPED_VALUES[rawLabel];
    }
    // For section labels (as defined by the frontend_library), we return the custom Cloud translation if it exists,
    // otherwise we fall back to the raw label translation or the raw label itself if no translation is found.
    if (SECTION_KEY_VALUES.includes(rawLabel)) {
      return SECTION_LABEL_TRANSLATION_MAP[rawLabel] ?? i18n._(rawLabel) ?? rawLabel;
    }
    // Backwards compatibility: for any other raw label, we first check if it exists in our custom Cloud translation map,
    // otherwise we fall back to the raw label translation or the raw label itself if no translation is found.
    return RAW_LABEL_TO_TRANSLATION_MAP[rawLabel.toLowerCase()] ?? i18n._(rawLabel) ?? rawLabel;
  };

  return (
    <SurveillanceTable
      xAxisData={data}
      formattedSurveillanceData={formattedSurveillanceData}
      translateFunction={translateTableLabel}
      handleChipClick={onEventClick}
      legendHeader={i18n._('hour')}
      medicineList={medicineList}
      preConsultationOrigins={preConsultationOrigins}
      preConsultationOriginsPosition={PRE_CONSULTATION_ORIGIN_START_POSITION}
      showMedicationEvolution={false}
      displayedSurveillanceKeys={currentWorkflow?.surveillanceFieldKeysToDisplay}
      genericFieldPaths={currentWorkflow?.genericFieldPaths}
      scoreResultFieldMap={scoreResultFieldMap}
    />
  );
};

export default SurveillanceTableDisplay;
